import { Paper, ThemeProvider, createTheme } from "@mui/material"
import { WaterlineInventoryItem } from "../types"
import { DataGrid, gridStringOrNumberComparator } from "@mui/x-data-grid"
import CompliancePlip from "../../../../components/CompliancePlip"
import { ComplianceStatus } from "../../../../types"
import { TableButton } from "../../../../components/Buttons.styled"
import { useState } from "react"
import { Link, useTheme } from "@mui/material"
import { useNavigate } from "react-router-dom"
import { updateWaterlineInventory } from "../../../../services/waterlineService"
import { useAppSelector } from "../../../../redux/store"
import { selectCurrentCustomerNumber } from "../../../../redux/customer"

type WaterlineInventoryCalendarProps = {
  inventory: WaterlineInventoryItem[] | null,
  getInventoryItems: () => Promise<void>,
  onAlert: (message: string) => void,
}

const tableTheme = createTheme({
  typography: {
    fontFamily: '"Open Sans", sans-serif',
    fontSize: 13
  },
  components: {
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: "bold"
        }
      }
    }
  }
})

const setItemToolTipMessage = (item: WaterlineInventoryItem) => {
  let message = 'Inactive'

  if (item.status == ComplianceStatus.Ok) {
    message = 'Up to date'
  } else if (item.status == ComplianceStatus.Expiring && item?.subcategory === 'TREATMENT') {
    message = 'Reorder before item is depleted'
  } else if (item.status == ComplianceStatus.Expiring && item?.subcategory === 'MAIL-TEST') {
    message = 'Reorder before next testing date'
  } else if (item.status == ComplianceStatus.Expired) {
    message = 'Passed replacement date'
  }

  return message
}

const WaterlineInventoryTable = (props: WaterlineInventoryCalendarProps) => {
  const { inventory, getInventoryItems, onAlert } = props
  const theme = useTheme()
  const navigate = useNavigate()
  const isLoading = !Boolean(inventory)
  const customerNumber = useAppSelector(selectCurrentCustomerNumber)

  const [loadingItemId, setLoadingItemId] = useState<number | null>(null)

  const handleMarkAsUsedActive = async (item: WaterlineInventoryItem) => {
    try {
      const itemCopy = structuredClone(item)
      itemCopy.active = !itemCopy.active

      setLoadingItemId(itemCopy.id)
  
      await updateWaterlineInventory(customerNumber, itemCopy)
      await getInventoryItems()
  
      setLoadingItemId(null)
    } catch (error) {
      setLoadingItemId(null)
      const message = 'There was an error updating the item'
      console.error(message)
      onAlert(message)
    }
  }

  return (
    <Paper>
      <ThemeProvider theme={tableTheme}>
        <DataGrid
          sx={{ borderLeft: "none", borderRight: "none", borderBottom: "none", borderRadius: "0" }}
          rows={inventory ?? []}
          columns={[
            {
              field: "status",
              headerName: "Status",
              sortable: true,
              width: 90,
              renderCell: (params) => (
                <CompliancePlip message={setItemToolTipMessage(params.row)} status={params.row.status} />
              ),
              align: "center",
              getSortComparator: (sortDirection) => {
                const modifier = sortDirection === 'desc' ? -1 : 1;
                return (a, b, cellParams1, cellParams2) => {
                  if (a === b) {
                    return 0;
                  }

                  if (a === 4 || a === null) {
                    return 1;
                  }

                  if (b === 4 || b === null) {
                    return -1;
                  }

                  return (
                    modifier *
                    gridStringOrNumberComparator(a, b, cellParams1, cellParams2)
                  );
                };
              },
            },
            {
              field: "child_customer_number",
              headerName: "Name",
              flex: 1,
              minWidth: 280,
              renderCell: (params) => (
                <Link component="button" onClick={() => navigate(`/corp/practice/${params.row.customer_number}/waterline-management`)} sx={{ color: theme.palette.linkColor.main, textDecoration: 'none' }}>
                  {params.row.childCustomerName.trim()} ({params.row.customer_number})
                </Link>
              )
            },
            {
              field: "name",
              headerName: "Item Name",
              flex: 1,
              minWidth: 250,
            },
            { field: "item_number", headerName: "Item Number", width: 100 },
            { field: "invoice_number", headerName: "Order Number", width: 110 },
            {
              field: "room_chair",
              headerName: "Room/Chair",
              flex: 1,
              minWidth: 80,
              renderCell: (params) => (
                <span>{params.row.room?.name ?? ''}</span>
              )
            },
            {
              field: "start_date",
              headerName: "Start Date",
              flex: 1,
              minWidth: 65,
              renderCell: (params) => (
                <span>
                  {
                    ['treatment', 'tablets'].includes(params.row.subcategory?.toLowerCase() ?? '') && params.row.activated_date
                      ? (new Date(params.row.activated_date)).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric'})
                      : ''
                  }
                </span>
              )
            },
            {
              field: "install_date",
              headerName: "Install Date",
              flex: 1,
              minWidth: 65,
              renderCell: (params) => (
                <span>
                  {
                    (params.row.subcategory?.toLowerCase() ?? '') === 'straws' && params.row.activated_date
                      ? (new Date(params.row.activated_date)).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric'})
                      : ''
                  }
                </span>
              )
            },
            {
              field: "test_date",
              headerName: "Test Date",
              flex: 1,
              minWidth: 65,
              renderCell: (params) => (
                <span>
                  {
                    ['mail-test', 'in-office'].includes(params.row.subcategory?.toLowerCase() ?? '') && params.row.activated_date
                      ? (new Date(params.row.activated_date)).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric'})
                      : ''
                  }
                </span>
              )
            },
            {
              field: "shipped_date",
              headerName: "Ship Date",
              flex: 1,
              minWidth: 65,
              renderCell: (params) => (
                <span>
                  {
                    params.row.shipped_date
                      ? (new Date(params.row.shipped_date)).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric'})
                      : ''
                  }
                </span>
              )
            },
            {
              field: "replacement_date",
              headerName: "Replacement Date",
              flex: 1,
              minWidth: 140,
              renderCell: (params) => (
                <span>
                  {
                    (params.row.subcategory?.toLowerCase() ?? '') === 'straws' && (params.row.activated_date || params.row.shipped_date)
                      ? (new Date((params.row.activated_date || params.row.shipped_date || ''))).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric'})
                      : ''
                  }
                </span>
              )
            },
            {
              field: "mark_as_used",
              headerName: "",
              flex: 1,
              minWidth: 120,
              renderCell: (params) => (
                <TableButton size="small" variant="contained" onClick={() => handleMarkAsUsedActive(params.row)} disabled={loadingItemId === params.row.id}>
                  Mark as {params.row.active ? 'Used' : 'Active'}
                </TableButton>
              )
            }
          ]}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 25,
              },
            },
            sorting: {
              sortModel: [
                {
                  field: 'status',
                  sort: 'desc'
                }
              ]
            }
          }}
          pageSizeOptions={[5, 10, 20, 25, 50, 100]}
          loading={isLoading}
          autoHeight
          density="compact"
        />
      </ThemeProvider>   
    </Paper>
  )
}

export default WaterlineInventoryTable
